import React, { useState, useCallback, useRef } from 'react';
import {
  Container, Typography, Box, TextField, Button, MenuItem, Alert,
  Paper, CircularProgress, Chip
} from '@mui/material';
import { Send as SendIcon, InsertDriveFile as InsertDriveFileIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import supportService from '../services/supportService';
import LoadingOverlay from '../components/common/LoadingOverlay';

const CATEGORIES = ['general', 'payment', 'booking', 'listing', 'account', 'technical', 'dispute', 'other'];

const SupportNewTicketPage = () => {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    subject: '',
    description: '',
    category: 'general',
    contactEmail: ''
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const subjectRef = useRef(null);
  const descriptionRef = useRef(null);
  const emailRef = useRef(null);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    setError(null);
  };

  const scrollToFirstError = (errors) => {
    if (errors.subject && subjectRef.current) {
      subjectRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      subjectRef.current.focus();
      return;
    }
    if (errors.description && descriptionRef.current) {
      descriptionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      descriptionRef.current.focus();
      return;
    }
    if (errors.contactEmail && emailRef.current) {
      emailRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      emailRef.current.focus();
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.subject.trim()) {
      errors.subject = t('support:errors.subjectRequired', 'Le sujet est requis.');
    }
    if (!form.description.trim()) {
      errors.description = t('support:errors.descriptionRequired', 'La description est requise.');
    }
    if (!user && !form.contactEmail.trim()) {
      errors.contactEmail = t('support:errors.emailRequired', 'L\'adresse email est requise.');
    }
    setFieldErrors(errors);
    return errors;
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles.length) {
      return;
    }
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'application/pdf': ['.pdf']
    },
    maxSize: 5 * 1024 * 1024,
    multiple: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      scrollToFirstError(errors);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('subject', form.subject);
      formData.append('description', form.description);
      formData.append('category', form.category);
      if (!user && form.contactEmail) {
        formData.append('contactEmail', form.contactEmail);
      }
      files.forEach(file => formData.append('attachments', file, file.name));

      const res = await supportService.createTicket(formData);
      if (res.success) {
        if (user) {
          setSuccess(t('support:ticket.created'));
          setTimeout(() => navigate(`/support/tickets/${res.data.id}`), 1500);
        } else {
          setSuccess(t('support:ticket.createdAnonymous'));
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || t('support:errors.sendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'flex-start',
          mb: 3,
          py: 2,
          px: 3,
          borderRadius: 2,
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
          {t('support:ticket.create')}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 0, color: 'white' }}>
          {t('support:ticket.createSubtitle', 'Décrivez votre problème et nous vous aiderons')}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {/* Email (anonymous only) */}
          {!user && (
            <TextField
              fullWidth
              label={t('support:ticket.email')}
              placeholder={t('support:ticket.emailPlaceholder')}
              value={form.contactEmail}
              onChange={handleChange('contactEmail')}
              helperText={fieldErrors.contactEmail || t('support:ticket.emailHelp')}
              error={Boolean(fieldErrors.contactEmail)}
              type="email"
              sx={{ mb: 2 }}
              inputRef={emailRef}
            />
          )}

          <TextField
            fullWidth
            label={t('support:ticket.subject')}
            placeholder={t('support:ticket.subjectPlaceholder')}
            value={form.subject}
            onChange={handleChange('subject')}
            helperText={fieldErrors.subject || t('support:ticket.subjectHelp', 'Décrivez l\'objet de votre demande')}
            error={Boolean(fieldErrors.subject)}
            sx={{ mb: 2 }}
            inputRef={subjectRef}
          />

          <TextField
            fullWidth
            select
            label={t('support:ticket.category')}
            value={form.category}
            onChange={handleChange('category')}
            sx={{ mb: 2 }}
          >
            {CATEGORIES.map(cat => (
              <MenuItem key={cat} value={cat}>
                {t(`support:categories.${cat}`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            multiline
            rows={5}
            label={t('support:ticket.description')}
            placeholder={t('support:ticket.descriptionPlaceholder')}
            value={form.description}
            onChange={handleChange('description')}
            helperText={fieldErrors.description || t('support:ticket.descriptionHelp', 'Détaillez votre problème')}
            error={Boolean(fieldErrors.description)}
            sx={{ mb: 2 }}
            inputRef={descriptionRef}
          />

          {/* File upload */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('support:ticket.attachments')}
            </Typography>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'border-color 0.2s ease, background-color 0.2s ease'
              }}
            >
              <input {...getInputProps()} style={{ display: 'none' }} />
              <Typography variant="body2" color="text.secondary">
                {isDragActive
                  ? t('support:ticket.dropFilesHere', 'Déposez vos fichiers ici...')
                  : t('support:ticket.dragOrClick', 'Glissez-déposez vos fichiers ou cliquez pour sélectionner')}
              </Typography>
              <Button variant="outlined" size="small" sx={{ mt: 2 }}>
                {t('support:ticket.browseFiles', 'Parcourir les fichiers')}
              </Button>
            </Box>

            {files.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {files.map((file, index) => (
                  <Chip
                    key={`${file.name}-${index}`}
                    label={`${file.name} (${Math.round(file.size / 1024)} KB)`}
                    icon={<InsertDriveFileIcon />}
                    onDelete={() => setFiles(files.filter((_, i) => i !== index))}
                    deleteIcon={<DeleteIcon />}
                    sx={{ maxWidth: '100%' }}
                  />
                ))}
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {t('support:ticket.attachmentsHelp')}
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? t('support:ticket.sending') : t('support:ticket.send')}
          </Button>
        </Box>
      </Paper>

      <LoadingOverlay
        isOpen={loading}
        title={t('support:ticket.creatingTitle', 'Création en cours...')}
        subtitle={t('support:ticket.creatingSubtitle', 'Nous créons votre ticket. Veuillez patienter...')}
        warning={t('support:ticket.doNotClose', 'Ne fermez pas cette page.')}
        primaryColor="#6200ee"
        accentColor="#03dac4"
      />
    </Container>
  );
};

export default SupportNewTicketPage;
